// A night's display (WS-47): the rise and fall of an auroral substorm, time-compressed. Pure functions of the cycle
// number n and the position in the cycle (u, 0..1); aurora.js integrates u and applies the result. Resort-agnostic;
// configured by resort.json -> aurora.cycle (every key optional). Sources: skill references/rendering.md.
// Nature, and what is shown:
//  growth    1-2 h      quiet arcs brighten a little and drift equatorward (100-250 m/s); the poleward arc closes in
//  onset     < minutes  the equatorward arc brightens suddenly at one place and the brightening spreads along it
//  expansion ~30 min    the bright region spreads poleward (a second arc leaves the first; where they still join is
//                       the westward travelling surge, 1-3 km/s); rays, folds, colour; repeated intensifications
//  recovery  1-3 h      fading into diffuse glow with pulsating patches; the arcs return poleward
// Position changes slowly and only in these directions; the compass bearing never changes.
export const PHASES = ['growth', 'onset', 'expansion', 'recovery'];
export const CYCLE_DEFAULTS = {
  default: false,                      // on at load (the guest's switch, a preset's aurora.auto and &auroraAuto override it)
  length: 90,                          // s: mean length of one cycle (nature: about 3 h, so ~120x faster)
  hours: 3,                            // h: what one cycle stands for (said in the interface)
  says: null,                          // the time scale in the package's own words (null: said in numbers)
  // the phase guide (a caption while it plays): on by default ({ default: false }: off until switched on; false: no
  // guide), the least time a caption stays (s), and the package's own copy per phase (null: the interface's strings
  // aurora.part.<phase> and aurora.part.<phase>.line, which are translated)
  guide: { default: true }, noteHold: 6, names: null, lines: null,
  vary: 0.15,                          // +- share: every cycle has its own length
  seed: 7,
  shares: [0.33, 0.04, 0.27, 0.36],    // growth, onset, expansion, recovery
  quiet: 0.2,                          // the quiet arc: this share of the way from the calm level to the guest's Strength
  growthRise: 0.25,                    // brightening during the growth phase (share of the way to the peak)
  peak: [0.72, 0.92],                  // the breakup's level, a share of the guest's Strength; one draw per cycle
  flash: 0.2,                          // the onset overshoots that level by this much, then decays: never above Strength
  drift: 150,                          // km: how far poleward the arcs stand when the growth phase begins
  driftShare: 0.35,                    // ...at most this share of the band's ground distance (a few degrees of height)
  near: 0.5, far: 1.4,                 // x offset of a poleward band: closed in at onset, at the end of the expansion
  ramp: 500,                           // km: length of the stretch where the leaving arc still joins (slope stays gentle)
  spread: 110,                         // km: the breakup starts within this distance of the band centre
  surge: 26,                           // km/s as shown: first speed of the surge (it slows down, as in nature)
  head: 0.35, headKm: 150,             // the surge head: extra brightness, half-width (km)
  lead: 14,                            // s of quiet arc before the breakup after the mode is switched on
  pulse: 0.3, pulseSpeed: 0.9,         // pulsating patches in the recovery: depth, rate (rad/s)
  even: [0.5, 0.65],                   // how far the brightness along the band is evened out: quiet arc, breakup
};
const rng = (a) => () => { a = (a + 0x6d2b79f5) | 0; let t = Math.imul(a ^ (a >>> 15), 1 | a); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
const c01 = (x) => Math.min(1, Math.max(0, x));
const sm = (x) => { x = c01(x); return x * x * (3 - 2 * x); };
const lerp = (a, b, t) => a + (b - a) * t;

/** The seeded draw of cycle n: length T (s), phase boundaries b, peak, breakup place s0 (km), intensifications. */
export function cycleParams(C, n) {
  const r = rng((C.seed | 0) * 7919 + n * 104729 + 1), [g, o, e] = C.shares;
  const b1 = g * (0.85 + 0.3 * r()), b2 = b1 + o, b3 = b2 + e * (0.85 + 0.3 * r());
  const P = { T: Math.max(8, C.length * (1 - C.vary + 2 * C.vary * r())), b: [b1, b2, b3], peak: lerp(C.peak[0], C.peak[1], r()), drift: 0.7 + 0.3 * r(), s0: (2 * r() - 1) * C.spread, launch: b1 + 0.3 * o };
  P.kicks = [{ u: P.launch, s: P.s0, amp: 1 }]; // the onset, then one or two intensifications
  const more = 1 + (r() < 0.55 ? 1 : 0);
  for (let i = 0; i < more; i++) P.kicks.push({ u: lerp(b2, b3, (i + 0.55 + 0.3 * r()) / (more + 0.6)), s: (2 * r() - 1) * C.spread * 1.6, amp: 0.45 + 0.4 * r() });
  return P;
}

/** The display at u of cycle n, continuous in u and across cycles. a: activity (0 quiet arc .. 1 this cycle's level);
 * eOut / eIn: offset factor of a poleward band outside / inside the stretch grown west / east (km) from s0; i, x, end:
 * phase index, progress, its end (u); note, noteX: the same for a caption (the breakup's stays C.noteHold s). */
export function cycleAt(C, n, u) {
  const P = cycleParams(C, n), [b1, b2, b3] = P.b;
  const env = (t, gap) => (t <= 0 ? 0 : 1.45 * (1 - Math.exp(-t / 1.4)) * Math.exp(-t / 11) * (1 - sm((t - 0.55 * gap) / (0.45 * gap))));
  const travel = (t) => (t <= 0 ? 0 : C.surge * (9 * (1 - Math.exp(-t / 9)) + 0.14 * t)); // km
  let kick = 0, head = { s: 0, amp: 0 };
  P.kicks.forEach((k, i) => {
    const t = (u - k.u) * P.T, v = k.amp * env(t, ((P.kicks[i + 1]?.u ?? b3 + 0.12) - k.u) * P.T);
    kick += v;
    if (v > head.amp) head = { s: k.s - travel(t), amp: v };
  });
  const run = travel((u - P.launch) * P.T), xe = c01((u - P.launch) / (b3 - P.launch));
  const o = { T: P.T, peak: P.peak, s0: P.s0, flash: kick, head, west: run, east: 0.45 * run, diffuse: 0, pulse: 0, boost: 0, even: C.even[1], drift: 0, eOut: C.near, eIn: C.near };
  if (u < b1) {
    const x = (o.x = u / b1);
    o.a = C.growthRise * x ** 1.6 * (1 + 0.25 * Math.sin(Math.PI * x) * Math.sin(9 * x + P.s0));
    o.drift = P.drift * (1 - sm(x)); o.eOut = o.eIn = lerp(1, C.near, sm(x)); o.even = C.even[0];
  } else if (u < b3) {
    const on = u < b2, x = (o.x = on ? (u - b1) / (b2 - b1) : (u - b2) / (b3 - b2));
    o.a = (on ? lerp(C.growthRise, 0.82, sm(x)) : 0.82 - 0.04 * x) + C.flash * kick;
    o.eIn = lerp(C.near, C.far, 1 - (1 - xe) ** 2.5); o.boost = sm(xe / 0.2);
    if (on) o.even = lerp(C.even[0], C.even[1], sm(x));
  } else {
    const x = (o.x = (u - b3) / (1 - b3));
    o.a = 0.78 * (1 - x) ** 1.5 * (1 + 0.3 * Math.sin(Math.PI * x) * Math.sin(11 * x + P.s0)) + C.flash * kick;
    o.drift = cycleParams(C, n + 1).drift * sm(x); o.eOut = lerp(C.near, 1, sm(x)); o.eIn = lerp(C.far, 1, sm(x));
    o.boost = 1 - sm(x / 0.45); o.even = C.even[1] * (1 - sm(x / 0.25)) + C.even[0] * sm(2.5 * x - 1.5); // patchy, then a quiet arc again
    o.diffuse = sm(x / 0.3) * (1 - sm((x - 0.78) / 0.22)); o.pulse = sm((x - 0.12) / 0.2) * (1 - sm((x - 0.7) / 0.25));
  }
  const e1 = Math.max(b2, Math.min(b3, b1 + C.noteHold / P.T));
  o.i = u < b1 ? 0 : u < b2 ? 1 : u < b3 ? 2 : 3; o.phase = PHASES[o.i]; o.end = [b1, b2, b3, 1][o.i];
  o.note = o.i && u < e1 ? 1 : o.i;
  o.noteX = o.note === 1 ? (u - b1) / (e1 - b1) : o.note === 2 ? (u - e1) / (b3 - e1) : o.x;
  return o;
}

/** u of a named phase's typical moment ('lead' = C.lead s before the onset), or the number given; null otherwise. */
export function phaseU(C, n, v) {
  const P = cycleParams(C, n), [b1, b2, b3] = P.b;
  const at = { lead: Math.max(0, b1 - C.lead / P.T), growth: 0.55 * b1, onset: b1 + 0.75 * (b2 - b1), expansion: lerp(b2, b3, 0.45), recovery: lerp(b3, 1, 0.42) }[v];
  return at ?? (v != null && v !== '' && Number.isFinite(+v) ? c01(+v) % 1 : null);
}
